import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import {
	MaptilerLayer
} from '@maptiler/leaflet-maptilersdk';

class ReportsMap {
	constructor(element) {
		this.element = element;

		this.config = window.stop18MapConfig || {};

		this.endpoint =
			element.dataset.endpoint || '';

		this.yearsEndpoint =
			element.dataset.yearsEndpoint || '';

		this.maptilerApiKey =
			this.config.maptilerApiKey || '';

		this.mapLanguage =
			this.config.language || 'pl';

		this.mapStyle =
			this.config.style || 'streets-v2';

		this.centerLat = parseFloat(
			element.dataset.centerLat || '52.0693'
		);

		this.centerLng = parseFloat(
			element.dataset.centerLng || '19.4803'
		);

		this.zoom = parseInt(
			element.dataset.zoom || '6',
			10
		);

		this.smallMax = parseInt(
			element.dataset.smallMax || '19',
			10
		);

		this.mediumMin = parseInt(
			element.dataset.mediumMin || '20',
			10
		);

		this.mediumMax = parseInt(
			element.dataset.mediumMax || '49',
			10
		);

		this.largeMin = parseInt(
			element.dataset.largeMin || '50',
			10
		);

		this.currentYear =
			element.dataset.currentYear || null;

		this.activeYear = this.currentYear
			? parseInt(this.currentYear, 10)
			: null;

		this.map = null;
		this.baseLayer = null;
		this.layer = null;
		this.abortController = null;

		/*
		 * Desktop z myszką:
		 * tooltip po najechaniu i popup po kliknięciu.
		 *
		 * Telefon/tablet:
		 * tylko popup po dotknięciu punktu.
		 */
		this.isDesktop = window.matchMedia(
			'(hover: hover) and (pointer: fine)'
		).matches;

		this.isTouchDevice =
			window.matchMedia(
				'(pointer: coarse)'
			).matches
			|| navigator.maxTouchPoints > 0;

		this.filterContainer =
			element
				.closest('.reports-map-widget')
				?.querySelector(
					'.js-reports-map-year-filter'
				)
			|| null;

		this.init();
	}

	init() {
		if (!this.endpoint) {
			console.error(
				'Nie ustawiono endpointu danych mapy.'
			);

			return;
		}

		if (!this.maptilerApiKey) {
			console.error(
				'Nie ustawiono klucza MapTiler API w ustawieniach WordPressa.'
			);

			return;
		}


		const polandBounds = L.latLngBounds(
			L.latLng(49.0, 14.12),
			L.latLng(54.84, 24.15)
		);

		this.map = L.map(
			this.element,
			{
				maxZoom: 12,

				// Blokowanie poza obszarem Polski.
				maxBounds: polandBounds,
				maxBoundsViscosity: 1.0,

				zoomSnap: 0.25,
				zoomDelta: 0.25,

				/*
				 * Interakcja jest początkowo wyłączona.
				 * Kliknięcie lub tapnięcie mapy ją aktywuje.
				 */
				scrollWheelZoom: false,
				doubleClickZoom: false,
				touchZoom: false,
				dragging: false,
				zoomControl: false
			}
		);

		this.bindMapInteractionEvents();

		/**
		 * Dopasowanie początkowego widoku do Polski.
		 */
		this.map.fitBounds(
			polandBounds,
			{
				padding: [10, 10],
				animate: false
			}
		);

		this.map.setZoom(
			this.map.getZoom() + 0.1
		);

		/**
		 * Zapobiega oddaleniu mapy bardziej
		 * niż widok całej Polski.
		 */
		const polandMinZoom =
			this.map.getZoom();

		this.map.setMinZoom(
			polandMinZoom
		);

		this.map.setMaxBounds(
			polandBounds
		);

		/**
		 * Dodatkowe zabezpieczenie po zmianie pozycji.
		 */
		this.map.on(
			'drag',
			() => {
				this.map.panInsideBounds(
					polandBounds,
					{
						animate: false
					}
				);
			}
		);

		this.map.on(
			'zoomend',
			() => {
				this.map.panInsideBounds(
					polandBounds,
					{
						animate: false
					}
				);
			}
		);

		L.control.zoom(
			{
				position: 'topright',
				zoomInTitle: 'Przybliż mapę',
				zoomOutTitle: 'Oddal mapę'
			}
		).addTo(this.map);

		this.addBaseLayer();

		this.layer =
			L.layerGroup().addTo(this.map);

		this.loadYearButtons();
		this.loadPoints();
		this.bindYearButtons();

		document.addEventListener(
			'wpcf7mailsent',
			() => {
				this.loadPoints();
			}
		);

		window.setTimeout(
			() => {
				if (!this.map) {
					return;
				}

				this.map.invalidateSize();

				this.map.fitBounds(
					polandBounds,
					{
						padding: [10, 10],
						animate: false
					}
				);

				this.map.setZoom(
					this.map.getZoom() + 0.1
				);

				this.map.setMinZoom(
					this.map.getZoom()
				);
			},
			300
		);
	}

	/**
	 * Obsługuje aktywowanie mapy po kliknięciu.
	 */
	bindMapInteractionEvents() {
		if (
			!this.map
			|| !this.element
		) {
			return;
		}

		const enableMapInteraction = () => {
			this.map.dragging.enable();
			this.map.scrollWheelZoom.enable();
			this.map.doubleClickZoom.enable();
			this.map.touchZoom.enable();

			this.element.dataset.mapInteractive =
				'true';
		};

		const disableMapInteraction = () => {
			this.map.dragging.disable();
			this.map.scrollWheelZoom.disable();
			this.map.doubleClickZoom.disable();
			this.map.touchZoom.disable();

			this.element.dataset.mapInteractive =
				'false';
		};

		/*
		 * Początkowo mapa nie przechwytuje kółka
		 * ani przeciągania myszy.
		 */
		disableMapInteraction();

		this.element.addEventListener(
			'click',
			(event) => {
				enableMapInteraction();

				/*
				 * Zapobiega natychmiastowemu wyłączeniu mapy
				 * przez zdarzenie kliknięcia na document.
				 */
				event.stopPropagation();
			}
		);

		document.addEventListener(
			'click',
			(event) => {
				if (
					this.element.contains(
						event.target
					)
				) {
					return;
				}

				disableMapInteraction();
			}
		);

		document.addEventListener(
			'keydown',
			(event) => {
				if (event.key !== 'Escape') {
					return;
				}

				disableMapInteraction();
			}
		);
	}

	addBaseLayer() {
		try {
			this.baseLayer =
				new MaptilerLayer(
					{
						apiKey:
							this.maptilerApiKey,

						style:
							'https://api.maptiler.com/maps/dataviz/style.json?key='
							+ this.maptilerApiKey,

						language:
							this.mapLanguage
					}
				);

			this.baseLayer.addTo(
				this.map
			);
		} catch (error) {
			console.error(
				'Nie udało się utworzyć warstwy MapTiler:',
				error
			);
		}
	}

	async loadYearButtons() {
		if (
			!this.yearsEndpoint
			|| !this.filterContainer
		) {
			return;
		}

		try {
			const response = await fetch(
				this.yearsEndpoint,
				{
					headers: {
						Accept:
							'application/json'
					}
				}
			);

			if (!response.ok) {
				throw new Error(
					`Błąd HTTP podczas pobierania lat: ${response.status}`
				);
			}

			const data =
				await response.json();

			const years =
				Array.isArray(data.years)
					? data.years
						.map(
							(year) =>
								parseInt(
									year,
									10
								)
						)
						.filter(
							(year) =>
								Number.isInteger(
									year
								)
						)
					: [];

			const hadActiveYear =
				Number.isInteger(
					this.activeYear
				);

			const hasActiveYear =
				hadActiveYear
				&& years.includes(
					this.activeYear
				);

			this.filterContainer
				.querySelectorAll(
					'.js-reports-map-year-btn:not([data-year="all"])'
				)
				.forEach(
					(button) => {
						button.remove();
					}
				);

			const allButton =
				this.filterContainer
					.querySelector(
						'[data-year="all"]'
					);

			if (
				hadActiveYear
				&& !hasActiveYear
			) {
				this.activeYear = null;
			}

			if (allButton) {
				const isActive =
					this.activeYear === null;

				allButton.classList.toggle(
					'is-active',
					isActive
				);

				allButton.setAttribute(
					'aria-pressed',
					String(isActive)
				);
			}

			years.forEach(
				(year) => {
					const button =
						document.createElement(
							'button'
						);

					button.type =
						'button';

					button.className =
						'reports-map-widget__year-btn js-reports-map-year-btn';

					button.dataset.year =
						String(year);

					button.textContent =
						String(year);

					button.setAttribute(
						'aria-label',
						`Pokaż zgłoszenia z roku ${year}`
					);

					button.setAttribute(
						'aria-pressed',
						String(
							year
							=== this.activeYear
						)
					);

					if (
						year
						=== this.activeYear
					) {
						button.classList.add(
							'is-active'
						);
					}

					if (allButton) {
						this.filterContainer
							.insertBefore(
								button,
								allButton
							);
					} else {
						this.filterContainer
							.appendChild(
								button
							);
					}
				}
			);

			this.bindYearButtons();

			if (
				hadActiveYear
				&& !hasActiveYear
			) {
				this.loadPoints();
			}
		} catch (error) {
			console.error(
				'Błąd pobierania dostępnych lat mapy:',
				error
			);
		}
	}

	bindYearButtons() {
		if (!this.filterContainer) {
			return;
		}

		this.filterContainer
			.querySelectorAll(
				'.js-reports-map-year-btn'
			)
			.forEach(
				(button) => {
					const freshButton =
						button.cloneNode(
							true
						);

					button.replaceWith(
						freshButton
					);

					freshButton.addEventListener(
						'click',
						() => {
							const yearValue =
								freshButton
									.dataset
									.year;

							this.activeYear =
								yearValue
								&& yearValue
									!== 'all'
									? parseInt(
										yearValue,
										10
									)
									: null;

							this.updateYearButtons(
								freshButton
							);

							if (this.layer) {
								this.layer
									.clearLayers();
							}

							this.loadPoints();

							document.dispatchEvent(
								new CustomEvent(
									'stop18:year-change',
									{
										detail: {
											year:
												this.activeYear
										}
									}
								)
							);
						}
					);
				}
			);
	}

	updateYearButtons(activeButton) {
		if (!this.filterContainer) {
			return;
		}

		this.filterContainer
			.querySelectorAll(
				'.js-reports-map-year-btn'
			)
			.forEach(
				(button) => {
					const isActive =
						button
						=== activeButton;

					button.classList.toggle(
						'is-active',
						isActive
					);

					button.setAttribute(
						'aria-pressed',
						String(isActive)
					);
				}
			);
	}

	async loadPoints() {
		if (!this.layer) {
			return;
		}

		if (this.abortController) {
			this.abortController.abort();
		}

		this.abortController =
			new AbortController();

		const { signal } =
			this.abortController;

		try {
			let url = this.endpoint;

			if (
				Number.isInteger(
					this.activeYear
				)
			) {
				const separator =
					url.includes('?')
						? '&'
						: '?';

				url +=
					`${separator}year=${this.activeYear}`;
			}

			const response = await fetch(
				url,
				{
					signal,
					headers: {
						Accept:
							'application/json'
					}
				}
			);

			if (!response.ok) {
				throw new Error(
					`Błąd HTTP podczas pobierania mapy: ${response.status}`
				);
			}

			const data =
				await response.json();

			this.renderPoints(
				Array.isArray(
					data.points
				)
					? data.points
					: []
			);
		} catch (error) {
			if (
				error.name
				!== 'AbortError'
			) {
				console.error(
					'Błąd pobierania punktów mapy:',
					error
				);
			}
		}
	}

	renderPoints(points) {
		if (!this.layer) {
			return;
		}

		this.layer.clearLayers();

		const groupedPoints =
			this.groupPointsByCity(
				points
			);

		groupedPoints.forEach(
			(point) => {
				const lat =
					parseFloat(
						point.lat
					);

				const lng =
					parseFloat(
						point.lng
					);

				const count =
					parseInt(
						point.count || '1',
						10
					);

				if (
					!Number.isFinite(lat)
					|| !Number.isFinite(lng)
				) {
					return;
				}

				const marker =
					L.circleMarker(
						[lat, lng],
						this.getMarkerOptions(
							count
						)
					);

				/*

				 *
				 * Na telefonie dotknięcie markera
				 * otwiera wyłącznie popup.
				 */
				if (
					this.isDesktop
					&& !this.isTouchDevice
				) {
					marker.bindTooltip(
						this.getTooltipContent(
							point
						),
						{
							direction: 'top',
							sticky: true,
							opacity: 0.95
						}
					);
				}

				marker.bindPopup(
					this.getPopupContent(
						point
					)
				);

				marker.on(
					'popupopen',
					(event) => {

						if (
							!this.isDesktop
							&& marker.getTooltip()
							&& marker.isTooltipOpen()
						) {
							marker.closeTooltip();
						}

						this.bindPopupLinks(
							event.popup
								.getElement()
						);
					}
				);

				marker.addTo(
					this.layer
				);
			}
		);
	}

	groupPointsByCity(points) {
		const grouped = {};

		points.forEach(
			(point) => {
				const city =
					String(
						point.city || ''
					).trim();

				const voivodeship =
					String(
						point.voivodeship
						|| ''
					).trim();

				if (!city) {
					return;
				}

				const key = [
					voivodeship
						.toLocaleLowerCase(
							'pl-PL'
						),
					city
						.toLocaleLowerCase(
							'pl-PL'
						)
				].join('-');

				if (!grouped[key]) {
					grouped[key] = {
						city,
						voivodeship,
						lat: point.lat,
						lng: point.lng,
						osm_url:
							point.osm_url
							|| '',
						count: 0,
						sale_points: []
					};
				}

				const pointCount =
					parseInt(
						point.count
							|| '1',
						10
					);

				grouped[key].count +=
					Number.isFinite(
						pointCount
					)
						? pointCount
						: 1;

				if (
					Array.isArray(
						point.sale_points
					)
				) {
					grouped[key]
						.sale_points
						.push(
							...point
								.sale_points
						);
				}

				if (point.sale_point) {
					grouped[key]
						.sale_points
						.push(
							point.sale_point
						);
				}

				if (
					!grouped[key].osm_url
					&& point.osm_url
				) {
					grouped[key].osm_url =
						point.osm_url;
				}
			}
		);

		return Object.values(
			grouped
		).map(
			(point) => ({
				...point,
				sale_points: [
					...new Set(
						point.sale_points
							.map(
								(item) =>
									String(
										item
									).trim()
							)
							.filter(
								Boolean
							)
					)
				]
			})
		);
	}

	getMarkerOptions(count) {
		if (count >= this.largeMin) {
			return {
				radius: 32,
				color: '#d5061c',
				fillColor: '#d5061c',
				weight: 1,
				opacity: 0.75,
				fillOpacity: 0.45
			};
		}

		if (
			count >= this.mediumMin
			&& count <= this.mediumMax
		) {
			return {
				radius: 22,
				color: '#f4945d',
				fillColor: '#f4945d',
				weight: 1,
				opacity: 0.75,
				fillOpacity: 0.45
			};
		}

		return {
			radius: 12,
			color: '#ffcc54',
			fillColor: '#ffcc54',
			weight: 1,
			opacity: 0.85,
			fillOpacity: 0.7
		};
	}

	getTooltipContent(point) {
		const city =
			point.city || '';

		const count =
			parseInt(
				point.count || '1',
				10
			);

		return `
			<div class="reports-map-widget__tooltip">
				<strong>${this.escapeHtml(city)}</strong>
				<br>
				<span>Liczba zgłoszeń: ${count}</span>
			</div>
		`;
	}

	getPopupContent(point) {
		const city =
			point.city || '';

		const voivodeship =
			point.voivodeship || '';

		const count =
			parseInt(
				point.count || '1',
				10
			);

		const salePoints =
			Array.isArray(
				point.sale_points
			)
				? point.sale_points.slice(
					0,
					5
				)
				: [];

		let html = `
			<div class="reports-map-widget__popup">
				<strong>${this.escapeHtml(city)}</strong>
				<br>
		`;

		if (voivodeship) {
			html += `
				<span>
					Województwo:
					${this.escapeHtml(voivodeship)}
				</span>
				<br>
			`;
		}

		html += `
			<span>Liczba zgłoszeń: ${count}</span>
		`;

		if (salePoints.length) {
			html += `
				<p>
					<strong>
						Zgłoszone punkty sprzedaży:
					</strong>
				</p>
				<ul>
			`;

			salePoints.forEach(
				(item) => {
					html += `
						<li>${this.escapeHtml(item)}</li>
					`;
				}
			);

			html += '</ul>';
		}

		html += '</div>';

		return html;
	}

	bindPopupLinks(container) {
		if (!container) {
			return;
		}

		container
			.querySelectorAll(
				'.js-open-osm-map'
			)
			.forEach(
				(link) => {
					link.addEventListener(
						'click',
						(event) => {
							event.preventDefault();
							event.stopPropagation();

							const url =
								link.dataset
									.osmUrl
								|| link.href;

							if (!url) {
								return;
							}

							window.open(
								url,
								'_blank',
								'noopener,noreferrer'
							);
						}
					);
				}
			);
	}

	getOpenStreetMapUrl(point) {
		const parsed =
			this.parseOpenStreetMapUrl(
				point.osm_url || ''
			);

		if (parsed) {
			return this.buildOpenStreetMapUrl(
				parsed.lat,
				parsed.lng,
				parsed.zoom
			);
		}

		const lat =
			parseFloat(
				point.lat
			);

		const lng =
			parseFloat(
				point.lng
			);

		if (
			!Number.isFinite(lat)
			|| !Number.isFinite(lng)
		) {
			return '';
		}

		return this.buildOpenStreetMapUrl(
			lat,
			lng
		);
	}

	parseOpenStreetMapUrl(value) {
		const hashPart =
			String(value || '')
				.split('#map=')[1]
			|| '';

		const parts =
			hashPart.split('/');

		if (parts.length < 3) {
			return null;
		}

		const zoom =
			parseInt(
				parts[0],
				10
			);

		const lat =
			parseFloat(
				parts[1]
			);

		const lng =
			parseFloat(
				parts[2]
			);

		if (
			!Number.isFinite(zoom)
			|| !Number.isFinite(lat)
			|| !Number.isFinite(lng)
		) {
			return null;
		}

		return {
			zoom,
			lat,
			lng
		};
	}

	buildOpenStreetMapUrl(
		lat,
		lng,
		zoom = 17
	) {
		return [
			'https://www.openstreetmap.org/',
			`#map=${zoom}/`,
			`${lat.toFixed(6)}/`,
			lng.toFixed(6)
		].join('');
	}

	escapeHtml(value) {
		return String(value)
			.replace(
				/&/g,
				'&amp;'
			)
			.replace(
				/</g,
				'&lt;'
			)
			.replace(
				/>/g,
				'&gt;'
			)
			.replace(
				/"/g,
				'&quot;'
			)
			.replace(
				/'/g,
				'&#039;'
			);
	}
}

const initializeReportsMaps = () => {
	document
		.querySelectorAll(
			'.js-reports-map'
		)
		.forEach(
			(element) => {
				if (
					element.dataset
						.reportsMapInitialized
					=== 'true'
				) {
					return;
				}

				element.dataset
					.reportsMapInitialized =
						'true';

				new ReportsMap(
					element
				);
			}
		);
};

if (
	document.readyState === 'loading'
) {
	document.addEventListener(
		'DOMContentLoaded',
		initializeReportsMaps
	);
} else {
	initializeReportsMaps();
}

document.addEventListener(
	'elementor/frontend/init',
	initializeReportsMaps
);